import React, { useEffect, useRef, useState } from "react";

import { createPortal } from "react-dom";

// Pausa com a tela já coberta, antes de a neblina começar a se abrir. É o único
// trecho da passagem sem animação CSS para escutar, então o único cronometrado
// por JS — e por isso o único que precisa checar movimento reduzido na mão. As
// outras duas fases são governadas pelo CSS, já zerado pela regra global de
// `prefers-reduced-motion`. Somada às animações (600ms de entrada + 800ms de
// saída), fecha a passagem em 1,6s.
const PAUSA_COBERTO = 200;

// Rede de segurança. A cortina cobre a tela INTEIRA e engole cliques de
// propósito, então uma que não termine é uma tela travada, não um enfeite que
// falhou. Ela depende de dois `animationend`, e animação CSS que não roda não
// emite evento nenhum: basta o elemento nascer em `display: none` num tema do
// host, uma extensão que desliga animações, ou o navegador descartar a animação
// numa aba em background. 3s dá folga de sobra para os 1,6s reais.
const LIMITE_SEGURANCA = 3000;

function prefereMenosMovimento() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Cortina de neblina da passagem de leva.
 *
 * Por que existe: sem ela a revelação da leva nova não tem duração NENHUMA. O
 * tabuleiro salta de 880 para 1660px, os nós da leva 2 montam e o caminho SVG
 * ganha pontos — tudo em UM frame, e no AVA esse frame é pintado (o refetch dos
 * itens acontece ao fechar o modal, e a cerimônia só abre no efeito seguinte).
 * O aluno vê a trilha nova piscar para existir.
 *
 * Esta cortina inverte a lógica: cobre a tela ANTES da troca, deixa a troca
 * acontecer invisível, e revela a trilha nova já montada.
 *
 * Ganho colateral: o crescimento do tabuleiro desloca todos os nós (posicionados
 * por `bottom`) de uma vez, e o que acontece com o scroll depois disso depende
 * do *scroll anchoring* do navegador — heurística que não se garante sem medir.
 * Atrás da cortina, o reposicionamento pode ser instantâneo e ninguém vê.
 *
 * Portal para o body, envolto em `.trilha-ludica` pelo mesmo motivo de
 * `CerimoniaCard`: fora da árvore do módulo os tokens não são herdados, e
 * `position: fixed` dentro de um ancestral com `transform` se posiciona contra
 * ele em vez da viewport.
 *
 * @param {Function} onCoberto chamado quando a neblina terminou de cobrir a tela
 *                             — é aqui que o host troca o que está por baixo
 * @param {Function} onFim     chamado quando a neblina terminou de se abrir
 */
function PassagemLeva({ onCoberto, onFim }) {
  const [fase, setFase] = useState("cobrindo"); // 'cobrindo'|'coberto'|'revelando'
  // Ref, não estado: só serve para não avisar duas vezes se o `animationend`
  // disparar mais de uma vez, e não deve provocar re-render.
  const jaAvisou = useRef(false);

  useEffect(() => {
    if (fase !== "coberto") return undefined;
    const id = setTimeout(
      () => setFase("revelando"),
      prefereMenosMovimento() ? 0 : PAUSA_COBERTO
    );
    return () => clearTimeout(id);
  }, [fase]);

  // Só na montagem: se a passagem inteira não tiver acontecido dentro do limite,
  // desiste e devolve a tela. `onCoberto` também é chamado, senão a troca de leva
  // ficaria pendente para sempre e o tabuleiro congelado na leva antiga — pior que
  // a troca à vista que a cortina existe para esconder.
  useEffect(() => {
    const id = setTimeout(() => {
      if (!jaAvisou.current) {
        jaAvisou.current = true;
        onCoberto();
      }
      onFim();
    }, LIMITE_SEGURANCA);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fases governadas por `animationend`, e não por `setTimeout`: assim a duração
  // mora só no CSS. Manter o mesmo número nos dois lugares é como a versão do
  // protótipo funcionava — eles empatavam por coincidência, e quem mudasse o CSS
  // sem mexer no JS fazia a neblina sumir no meio da transição.
  function aoTerminarAnimacao(evento) {
    // O filtro por nome é OBRIGATÓRIO: o campo de neblina filho tem uma deriva
    // ambiente infinita, e animação infinita não emite `animationend` — mas sob
    // `prefers-reduced-motion` a regra global do módulo força
    // `animation-iteration-count: 1`, ela TERMINA, e o evento borbulharia até
    // aqui disparando a fase errada. Bug que só apareceria para quem usa
    // movimento reduzido.
    if (evento.animationName === "tl-neblina-entra") {
      if (!jaAvisou.current) {
        jaAvisou.current = true;
        onCoberto();
      }
      setFase("coberto");
    } else if (evento.animationName === "tl-neblina-sai") {
      onFim();
    }
  }

  return createPortal(
    <div className="trilha-ludica">
      <div
        className={`trilha-ludica__passagem trilha-ludica__passagem--${fase}`}
        onAnimationEnd={aoTerminarAnimacao}
        aria-hidden="true"
        data-testid="trilha-passagem-leva"
      >
        {/* Campo de neblina em gradientes (ver o CSS), e não uma ilustração:
            neblina não tem silhueta — é densidade irregular, que é o que radiais
            sobrepostos fazem bem. Houve um `nuvem-topo-trilha.svg` no módulo, uma
            faixa de nuvem desenhada para o topo do tabuleiro vertical; esticá-lo
            para a viewport produzia listras alongadas, que leem como borrão dando
            zoom. Ele saiu junto com o eixo vertical em 02/08/2026, e a névoa que
            avisa "a trilha continua" seguiu o mesmo caminho — gradiente. */}
        <div className="trilha-ludica__passagem-neblina" />
      </div>
    </div>,
    document.body
  );
}

export default PassagemLeva;
